<template>
    <div class="rounded-lg border border-slate-200 bg-white p-5 transition-all hover:border-blue-600 hover:ring-1 hover:ring-blue-600 dark:border-slate-800 dark:bg-slate-950 flex flex-col">
        <Link
            :href="subcategory.url"
            class="block flex-1"
        >
            <div class="mb-3 flex items-center justify-between relative">
                <div class="flex items-center gap-x-3">
                    <h3 class="text-lg font-medium text-slate-700 dark:text-white hover:text-blue-600 dark:hover:text-blue-600">
                        <span>{{ subcategory.name }}</span>
                    </h3>
                </div>
                <!-- Icon positioned at top right -->
                <div class="absolute top-0 right-0" :title="getIconTitle()">
                    <component
                        v-if="iconComponent"
                        :is="iconComponent"
                        class="h-12 w-12 text-gray-700"
                        :stroke-width="1.5"
                    />
                </div>
            </div>

            <div class="mt-2 flex-1 overflow-hidden relative">
                <template v-if="subcategory.subsubcategories?.length">
                    <div
                        v-for="subsubcat in displayedSubsubcategories"
                        :key="subsubcat.id"
                        class="flex justify-between items-center"
                    >
                        <Link
                            :href="subsubcat.url"
                            class="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-600"
                        >
                            <span>{{ subsubcat.name }}</span>
                        </Link>
                    </div>
                </template>
                <div v-else class="text-sm text-slate-500 dark:text-slate-400">
                    Geen subcategorieën beschikbaar
                </div>

                <!-- Subcat counter moved to bottom right -->
                <div v-if="subcategory.total_subsubcategories > 5" class="absolute bottom-0 right-0 text-sm text-slate-500 dark:text-slate-400 text-right whitespace-nowrap">
                    +{{ subcategory.total_subsubcategories - 5 }} ...
                </div>
            </div>
        </Link>

        <!-- Admin controls for icon matching -->
        <div v-if="page.props.auth?.user?.admin === 1" class="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
            <div class="flex flex-col gap-2">
                <!-- Update Icon Form using Inertia -->
                <div class="mb-2">
                    <div class="flex items-center gap-2">
                        <input
                            type="text"
                            v-model="updateIconForm.icon"
                            class="flex-1 px-2 py-1 text-sm border border-slate-300 rounded dark:bg-slate-800 dark:border-slate-600"
                            placeholder="Icon name"
                        />
                        <button
                            @click="submitUpdateIconForm"
                            class="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {Link, useForm, usePage} from '@inertiajs/vue3'
import {computed, ref, onMounted} from 'vue'
import ClockIcon from '@/Components/Icons/ClockIcon.vue'
import {getIcon} from '@/Components/Icons/IconRegistry'

const props = defineProps({
    category: {
        type: Object,
        required: true
    },
    subcategory: {
        type: Object,
        required: true
    },
    showRecalculateButton: {
        type: Boolean,
        default: false
    }
})

// Get the page object from Inertia
const page = usePage()

// Ref for the icon component
const iconComponent = ref(null)
const iconName = ref(props.subcategory?.icon || '')
const isRecalculating = ref(false)
const recalculationError = ref('')
const recalculationSuccess = ref(false)
const updateSuccess = ref(false)

// Check if the current user is an admin
const isAdmin = computed(() => {
    return page.props.auth &&
           page.props.auth.user &&
           page.props.auth.user.admin === 1;
})

// Create Inertia forms for icon management
const updateIconForm = useForm({
    // Initialize with empty string
    icon: ''
})

// Function to get icon title with icon name
const getIconTitle = () => {
    if (props.subcategory && props.subcategory.icon) {
        if (props.subcategory.icon.name) {
            return props.subcategory.icon.name;
        } else if (typeof props.subcategory.icon === 'string') {
            return props.subcategory.icon;
        }
    }
    return 'Icon';
}

// Function to submit the update icon form
const submitUpdateIconForm = () => {
    updateIconForm.post(`/categories/${props.subcategory.id}/update-icon`, {
        preserveScroll: true,
        onSuccess: () => {
            // Reload the page to show the updated icon
            window.location.reload()
        }
    })
}

// Load the icon component when the component is mounted
onMounted(async () => {
    // Debug admin controls
    // console.log('Admin Controls Debug:', {
    //     isAdmin: isAdmin.value,
    //     showRecalculateButton: props.showRecalculateButton,
    //     cookies: document.cookie
    // })

    // Debug: Log the subcategory object to see its structure
    // console.log('Subcategory object:', JSON.stringify(props.subcategory, null, 2))
    // console.log('Icon value:', JSON.stringify(props.subcategory?.icon, null, 2))

    // Set the icon name in the form
    if (props.subcategory?.icon) {
        // The icon is an object with a name property
        if (props.subcategory.icon.name) {
            updateIconForm.icon = props.subcategory.icon.name
        } else if (typeof props.subcategory.icon === 'string') {
            updateIconForm.icon = props.subcategory.icon
        } else {
            updateIconForm.icon = ''
        }
    } else {
        updateIconForm.icon = ''
    }

    // Load the icon for the subcategory if available
    if (props.subcategory && props.subcategory.icon) {
        try {
            // The icon component name is stored in icon.componentName
            if (props.subcategory.icon.componentName) {
                iconComponent.value = await getIcon(props.subcategory.icon.componentName)
            } else if (props.subcategory.icon_component) {
                // Fallback to old structure if it exists
                iconComponent.value = await getIcon(props.subcategory.icon_component)
            } else {
                console.log('No icon component name found')
            }
        } catch (error) {
            console.error('Error loading the subcategory card icon:', error)
        }
    }
})

const displayedSubsubcategories = computed(() => {
    return props.subcategory.subsubcategories?.slice(0, 5) || []
})
</script>
